/**
 * @module KarrierePage
 * @description Öffentliche Karriere-Seite — Hero, Vorteile, Stellen, Bewerbungsformular, DSGVO.
 *   Kein Login nötig, Design ähnlich FlexLandingPage.
 * @route /karriere
 * @since v2.219.0
 * @updated v2.231.0 — Hero, Vorteile, DSGVO, Standort, Config-driven
 * @updated v2.846.0 — #1779: WeShip-Logo, konfigurierbare Lebenslauf-Pflicht,
 *   Quellen aus Config (inkl. willhaben.at), durchgängige Du-Ansprache, i18n (de/en).
 */
import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

const VORTEILE_ICONS = ['\u{1F3E2}', '\u{1F4B0}', '\u{1F91D}', '\u{1F680}', '\u{1F389}', '\u2615'];

// Fallback, falls der Endpoint (noch) keine Quellen liefert — Werte identisch zum Backend-Default.
const QUELLEN_FALLBACK = [
  { value: 'website', label: 'Website' },
  { value: 'jobs.weship.eu', label: 'jobs.weship.eu' },
  { value: 'willhaben.at', label: 'willhaben.at' },
  { value: 'empfehlung', label: 'Empfehlung' },
  { value: 'ams', label: 'AMS' },
  { value: 'initiativ', label: 'Initiativbewerbung' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export default function KarrierePage() {
  const { t, language, setLanguage } = useLanguage();
  useEffect(() => { if (language !== 'de') setLanguage('de'); }, []); // DE als Standard (wie /jobs)

  const [stellen, setStellen] = useState([]);
  const [config, setConfig] = useState({});
  const [aktiv, setAktiv] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const formRef = useRef(null);

  const [form, setForm] = useState({
    vorname: '', nachname: '', email: '', telefon: '',
    position: '', stelle_id: '', anschreiben: '', quelle: '', fruehester_start: '',
    dsgvo: false,
  });
  const [cv, setCv] = useState(null);

  const TYP_LABELS = {
    vollzeit: t('karriere.typ.vollzeit', 'Vollzeit'),
    teilzeit: t('karriere.typ.teilzeit', 'Teilzeit'),
    lehrling: t('karriere.typ.lehrling', 'Lehrling'),
    praktikum: t('karriere.typ.praktikum', 'Praktikum'),
    geringfuegig: t('karriere.typ.geringfuegig', 'Geringfügig'),
  };

  const ABLAUF_SCHRITTE = [
    { nr: 1, icon: '\u{1F4E8}', title: t('karriere.ablauf.1.titel', 'Bewerben'), desc: t('karriere.ablauf.1.text', 'Bewerbung online einreichen') },
    { nr: 2, icon: '\u{1F4AC}', title: t('karriere.ablauf.2.titel', 'Bewerbungsgespräch'), desc: t('karriere.ablauf.2.text', 'Persönliches Kennenlernen vor Ort') },
    { nr: 3, icon: '\u{1F3ED}', title: t('karriere.ablauf.3.titel', 'Probearbeitstag'), desc: t('karriere.ablauf.3.text', 'Ein Tag im Team mitarbeiten') },
    { nr: 4, icon: '\u{1F389}', title: t('karriere.ablauf.4.titel', 'Start'), desc: t('karriere.ablauf.4.text', 'Willkommen bei WeShip!') },
  ];

  useEffect(() => {
    fetch(`${API}/api/public/recruiting/stellen`)
      .then(r => r.json())
      .then(data => {
        setAktiv(data.aktiv !== false);
        setStellen(data.stellen || []);
        setConfig(data.config || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Default TRUE: solange die Config nicht explizit "aus" sagt, ist der Lebenslauf Pflicht.
  const cvPflicht = config.karriere_lebenslauf_pflicht !== false && config.karriere_lebenslauf_pflicht !== 'false';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vorname.trim() || !form.nachname.trim() || !form.email.trim() || !form.telefon.trim()) {
      setError(t('karriere.fehler.pflichtfelder', 'Bitte fülle alle Pflichtfelder aus.'));
      return;
    }
    if (cvPflicht && !cv) {
      setError(t('karriere.fehler.lebenslauf', 'Bitte hänge deinen Lebenslauf an (PDF oder DOC).'));
      return;
    }
    if (!form.dsgvo) {
      setError(t('karriere.fehler.dsgvo', 'Bitte stimme der Datenschutzerklärung zu.'));
      return;
    }
    setSending(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append('vorname', form.vorname.trim());
      fd.append('nachname', form.nachname.trim());
      fd.append('email', form.email.trim());
      fd.append('telefon', form.telefon.trim());
      if (form.position) fd.append('position', form.position);
      if (form.stelle_id) fd.append('stelle_id', form.stelle_id);
      if (form.anschreiben.trim()) fd.append('anschreiben', form.anschreiben.trim());
      if (form.quelle) fd.append('quelle', form.quelle);
      if (form.fruehester_start) fd.append('fruehester_start', form.fruehester_start);
      fd.append('dsgvo_zustimmung', 'true');
      if (cv) fd.append('lebenslauf', cv);

      const res = await fetch(`${API}/api/public/recruiting/bewerben`, { method: 'POST', body: fd });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('karriere.fehler.allgemein', 'Ein Fehler ist aufgetreten.'));
      }
    } catch {
      setError(t('karriere.fehler.verbindung', 'Verbindungsfehler. Bitte versuche es später erneut.'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="animate-spin w-8 h-8 border-3 border-t-transparent rounded-full" style={{ borderColor: '#338edc', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!aktiv) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="text-center">
          <div className="text-4xl mb-4">{'\u{1F6A7}'}</div>
          <h1 className="text-xl font-bold" style={{ color: '#1A1A2E' }}>
            {t('karriere.inaktiv.titel', 'Karriereseite derzeit nicht verfügbar')}
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>
            {t('karriere.inaktiv.text', 'Bitte schau später wieder vorbei.')}
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="bg-white rounded-2xl border p-10 max-w-md w-full mx-4 text-center" style={{ borderColor: '#E5E7EB' }}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl" style={{ background: '#ECFDF5' }}>
            {'\u2705'}
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: '#1A1A2E' }}>
            {t('karriere.danke.titel', 'Bewerbung eingegangen!')}
          </h2>
          <p style={{ color: '#4B5563' }}>
            {t('karriere.danke.text', 'Vielen Dank für deine Bewerbung, {{vorname}}. Wir melden uns in Kürze bei dir.', { vorname: form.vorname })}
          </p>
        </div>
      </div>
    );
  }

  const heroTitel = config.karriere_hero_titel || t('karriere.hero.titel', 'Karriere bei WeShip');
  const heroText = config.karriere_hero_text || t('karriere.hero.text', 'Werde Teil unseres Teams! Wir suchen motivierte Teammitglieder für unser Fulfillment-Center.');
  const vorteile = (() => {
    const v = config.karriere_vorteile;
    if (Array.isArray(v)) return v;
    try { return JSON.parse(v || '[]'); } catch { return []; }
  })();
  const adresse = config.karriere_adresse || '';
  const mapsEmbed = config.karriere_google_maps_embed || '';
  const youtubeVideoId = config.karriere_youtube_video_id || '';
  const loeschfrist = config.recruiting_dsgvo_loeschfrist_tage || 180;
  const quellen = Array.isArray(config.karriere_quellen) && config.karriere_quellen.length
    ? config.karriere_quellen
    : QUELLEN_FALLBACK;

  return (
    <div className="min-h-screen" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <img src="/weship-logo.png" alt="WeShip Fulfillment" className="h-9 w-auto" />
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#1A1A2E' }}>{heroTitel}</h1>
          <p className="text-lg max-w-2xl mx-auto mb-8" style={{ color: '#4B5563' }}>{heroText}</p>
          <button
            onClick={scrollToForm}
            className="px-8 py-3.5 rounded-xl text-white font-semibold text-base"
            style={{ background: '#338edc' }}
          >
            {t('karriere.hero.cta', 'Jetzt bewerben')}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Vorteile */}
        {vorteile.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-6" style={{ color: '#1A1A2E' }}>
              {t('karriere.vorteile.titel', 'Warum WeShip?')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vorteile.map((v, i) => (
                <div key={i} className="bg-white rounded-xl border p-5 text-center" style={{ borderColor: '#E5E7EB' }}>
                  <div className="text-3xl mb-2">{VORTEILE_ICONS[i] || '\u2B50'}</div>
                  <p className="font-semibold text-sm" style={{ color: '#1A1A2E' }}>{v}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YouTube Video */}
        {youtubeVideoId && (
          <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                width="100%" height="100%"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="WeShip Video"
              />
            </div>
          </div>
        )}

        {/* Ablauf — So funktioniert's */}
        <div>
          <h2 className="text-2xl font-bold text-center mb-6" style={{ color: '#1A1A2E' }}>
            {t('karriere.ablauf.titel', "So läuft's ab")}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {ABLAUF_SCHRITTE.map((s, i) => (
              <div key={i} className="bg-white rounded-xl border p-5 text-center relative" style={{ borderColor: '#E5E7EB' }}>
                <div className="text-3xl mb-2">{s.icon}</div>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mx-auto mb-2"
                  style={{ background: '#338edc' }}>
                  {s.nr}
                </div>
                <p className="font-semibold text-sm" style={{ color: '#1A1A2E' }}>{s.title}</p>
                <p className="text-xs mt-1" style={{ color: '#6B7280' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Offene Stellen */}
        {stellen.length > 0 && (
          <div className="bg-white rounded-2xl border p-6" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-lg font-bold mb-4" style={{ color: '#1A1A2E' }}>
              {t('karriere.stellen.titel', 'Offene Stellen')}
            </h2>
            <div className="space-y-3">
              {stellen.map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor: '#E5E7EB' }}>
                  <span className="text-xl">{'\u{1F4E6}'}</span>
                  <div className="flex-1">
                    <div className="font-semibold" style={{ color: '#1A1A2E' }}>{s.titel}</div>
                    {s.beschreibung && <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{s.beschreibung}</p>}
                  </div>
                  {s.typ && (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#EBF4FD', color: '#338edc' }}>
                      {TYP_LABELS[s.typ] || s.typ}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bewerbungsformular */}
        <div ref={formRef} className="bg-white rounded-2xl border p-6" style={{ borderColor: '#E5E7EB' }}>
          <h2 className="text-lg font-bold mb-6" style={{ color: '#1A1A2E' }}>
            {t('karriere.formular.titel', 'Bewerbungsformular')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.vorname', 'Vorname')} *
                </label>
                <input name="vorname" value={form.vorname} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder={t('karriere.feld.vornamePlatzhalter', 'Max')} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.nachname', 'Nachname')} *
                </label>
                <input name="nachname" value={form.nachname} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder={t('karriere.feld.nachnamePlatzhalter', 'Mustermann')} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.email', 'E-Mail')} *
                </label>
                <input name="email" type="email" value={form.email} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder={t('karriere.feld.emailPlatzhalter', 'max@beispiel.at')} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.telefon', 'Telefon')} *
                </label>
                <input name="telefon" value={form.telefon} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder={t('karriere.feld.telefonPlatzhalter', '+43 664 ...')} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.position', 'Position')} *
                </label>
                <select name="position" value={form.position} onChange={(e) => {
                    const sel = stellen.find(s => s.titel === e.target.value);
                    setForm(prev => ({ ...prev, position: e.target.value, stelle_id: sel ? sel.id : '' }));
                  }} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}>
                  <option value="">{t('karriere.feld.bitteWaehlen', 'Bitte wählen...')}</option>
                  {stellen.map((s) => <option key={s.id || s.titel} value={s.titel}>{s.titel}{s.typ ? ` (${TYP_LABELS[s.typ] || s.typ})` : ''}</option>)}
                  <option value="Sonstiges">{t('karriere.feld.sonstiges', 'Sonstiges')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.start', 'Frühester Start')}
                </label>
                <input name="fruehester_start" type="date" value={form.fruehester_start} onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.quelle', 'Wie hast du uns gefunden?')}
                </label>
                <select name="quelle" value={form.quelle} onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}>
                  <option value="">{t('karriere.feld.bitteWaehlen', 'Bitte wählen...')}</option>
                  {quellen.map(q => <option key={q.value} value={q.value}>{q.label || q.value}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                  {t('karriere.feld.lebenslauf', 'Lebenslauf (PDF/DOC)')}{cvPflicht ? ' *' : ''}
                </label>
                <input type="file" accept=".pdf,.doc,.docx" required={cvPflicht}
                  onChange={(e) => setCv(e.target.files[0] || null)}
                  className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border file:text-sm file:font-medium"
                  style={{ color: '#4B5563' }} />
                {cvPflicht && (
                  <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                    {t('karriere.feld.lebenslaufPflichtHinweis', 'Ohne Lebenslauf können wir deine Bewerbung leider nicht annehmen.')}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>
                {t('karriere.feld.nachricht', 'Nachricht')}
              </label>
              <textarea name="anschreiben" value={form.anschreiben} onChange={handleChange} rows={4}
                className="w-full px-3 py-2.5 rounded-lg border text-sm resize-none" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                placeholder={t('karriere.feld.nachrichtPlatzhalter', 'Warum möchtest du bei WeShip arbeiten?')} />
            </div>

            {/* DSGVO Checkbox — Inhalt und rechtliche Aussage unverändert (#1779) */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" name="dsgvo" checked={form.dsgvo} onChange={handleChange}
                className="mt-1 w-4 h-4 rounded border" style={{ accentColor: '#338edc' }} />
              <span className="text-xs leading-relaxed" style={{ color: '#4B5563' }}>
                {t('karriere.dsgvo', 'Ich stimme der Verarbeitung meiner Daten gemäß der Datenschutzgrundverordnung (DSGVO) zu. Meine Daten werden ausschließlich für den Bewerbungsprozess verwendet und nach {{tage}} Tagen automatisch gelöscht. *', { tage: loeschfrist })}
              </span>
            </label>

            {error && (
              <div className="rounded-lg p-3 text-sm font-medium" style={{ background: '#FEF2F2', color: '#991B1B' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={sending}
              className="w-full py-3 rounded-xl text-white font-semibold text-base transition-colors disabled:opacity-60"
              style={{ background: '#338edc' }}>
              {sending ? t('karriere.formular.sendet', 'Wird gesendet...') : t('karriere.formular.absenden', 'Bewerbung absenden')}
            </button>
          </form>
        </div>

        {/* Standort */}
        {(adresse || mapsEmbed) && (
          <div className="bg-white rounded-2xl border p-6" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-lg font-bold mb-4" style={{ color: '#1A1A2E' }}>
              {t('karriere.standort.titel', 'Unser Standort')}
            </h2>
            {adresse && <p className="text-sm mb-4" style={{ color: '#4B5563' }}>{adresse}</p>}
            {mapsEmbed && (
              <div className="rounded-xl overflow-hidden" style={{ height: 300 }}>
                <iframe
                  src={mapsEmbed}
                  width="100%" height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Standort"
                />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <p className="text-xs text-center pb-6" style={{ color: '#9CA3AF' }}>
          WeShip GmbH {'\u2022'} {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
